import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useListEdition } from "@planzone/view-models";

import { UseCases } from "@planzone/core";
import { DateTimeInput } from "@common/dateTimeInput";
import { Safeguard } from "@scenes/safeguard";
import { Loader } from "@common/loader";
import { Props } from "./types";
import styles from "./styles.scss";

function ProjectEdition({ project }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, editList } = useListEdition();
  const [nextList, setList] = React.useState(project.rootList);
  const zone = project.timeZone || DateTime.local().zoneName;
  // Bounds are hour-precise (e.g. an HOUR-granularity constraint), not
  // necessarily local midnight, while DateTimeInput only lets the user pick a
  // day (committed at noon). The picked instant must be clamped to the bound's
  // exact instant when the picked day is the bound's day, otherwise it can land
  // before/after the real bound and be rejected by the backend.
  const boundDateTime = (iso?: string | null) => {
    if (!iso) return undefined;
    const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(zone);
    return dt.isValid ? dt : undefined;
  };
  const toCalendarDate = (dt?: DateTime) =>
    dt ? { year: dt.year, month: dt.month - 1, day: dt.day } : undefined;
  const clamp = (iso: string, min?: DateTime, max?: DateTime) => {
    const picked = DateTime.fromISO(iso, { zone: "utc" });
    const clamped =
      min && picked < min ? min : max && picked > max ? max : picked;
    return clamped.toUTC().toISO() ?? "";
  };
  const startMinBound = boundDateTime(nextList.minStartDate);
  const startMaxBound = boundDateTime(
    nextList.maxStartDate || nextList.endDate || nextList.maxEndDate,
  );
  const endMinBound = boundDateTime(
    nextList.minEndDate || nextList.startDate || nextList.minStartDate,
  );

  return (
    <Safeguard useCases={[UseCases.editProject]}>
      {({ canEditProject }) => (
        <div className={styles.projectEdition}>
          {isRequestPending && (
            <div className={styles.loader}>
              <div className={styles.loaderMain}>
                <Loader />
              </div>
            </div>
          )}
          <div>
            <h1 className={styles.title}>
              {t("projectPage.ganttPage.projectEdition.title")}
            </h1>
          </div>
          <div className={styles.main}>
            <div className={styles.group}>
              <p className={styles.text}>
                {t("projectPage.ganttPage.projectEdition.message")}
              </p>
            </div>
            {canEditProject && (
              <React.Fragment>
                <div className={styles.group}>
                  <p className={styles.text}>
                    {t("projectPage.ganttPage.projectEdition.define")}
                  </p>
                  <DateTimeInput
                    dateTime={nextList.startDate}
                    projectTimezone={zone}
                    placeholder={""}
                    minDate={toCalendarDate(startMinBound)}
                    maxDate={toCalendarDate(startMaxBound)}
                    input={(startDate) =>
                      setList({
                        ...nextList,
                        startDate: clamp(
                          startDate,
                          startMinBound,
                          startMaxBound,
                        ),
                      })
                    }
                  />
                  <p className={styles.text}>
                    {t("projectPage.ganttPage.projectEdition.startDate")}
                  </p>
                </div>
                <div className={styles.group}>
                  <p className={styles.text}>
                    {t("projectPage.ganttPage.projectEdition.define")}
                  </p>
                  <DateTimeInput
                    dateTime={nextList.endDate}
                    projectTimezone={zone}
                    placeholder={""}
                    minDate={toCalendarDate(endMinBound)}
                    input={(endDate) =>
                      setList({
                        ...nextList,
                        endDate: clamp(endDate, endMinBound),
                      })
                    }
                  />
                  <p className={styles.text}>
                    {t("projectPage.ganttPage.projectEdition.endDate")}
                  </p>
                </div>
                <div className={styles.buttons}>
                  <div
                    className={styles.button}
                    onClick={() =>
                      editList({ list: project.rootList, nextList })
                    }
                  >
                    {t("projectPage.ganttPage.projectEdition.confirm")}
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </Safeguard>
  );
}

export { ProjectEdition };
